import React from 'react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es as esLocale } from 'date-fns/locale';
import { FORMATS } from '../../../constants';
import { PtrnCampoEditableDatetime } from '../../../patronTypes';
import { PtrnPropsUtil } from '../../../patronUtils';
import { EditableProps } from '../campoEditableTypes';
import { useInputStyles } from '../campoEditableUtils';
import { Stack } from '@mui/material';
import { parse } from 'date-fns';
function CampoEditableDateTime(
   props: {
      campoConfig: PtrnCampoEditableDatetime;
   } & EditableProps & {
      ErrorComponent?: React.ReactNode;
   }
) {
   const { campoConfig, ...editableProps } = props;
   const classes = useInputStyles();

   const auxParam = {
      camposData: editableProps.camposData,
      campoConfig: campoConfig,
      ptrnRef: editableProps.ptrnRef,
      ruta: editableProps.ruta,
      index: editableProps.index
   };

   const handleChange = (newValue: Date | null) => {
      editableProps.onChange(campoConfig.name, newValue, auxParam);
   };

   const id = PtrnPropsUtil.genIdCampoEditable(campoConfig);

   const errorAdornments = props.ErrorComponent ? props.ErrorComponent : undefined;
   const handleOnBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
      if (editableProps.onBlur) {
         const name = e.target.name;
         const dateStr = e.target.value;
         const newValue = parse(dateStr, FORMATS.datetime, new Date());
         editableProps.onBlur(name, newValue, auxParam);
      }
   };
   // Helper function to merge adornments
   const mergeAdornments = (...adornments: React.ReactNode[]) => {
      const nonNullAdornments = adornments.filter((el) => el != null);
      if (nonNullAdornments.length === 0) {
         return null;
      }

      if (nonNullAdornments.length === 1) {
         return nonNullAdornments[0];
      }

      return (
         <Stack direction="row" spacing={1} alignItems="center">
            {nonNullAdornments.map((adornment, index) => (
               <React.Fragment key={index}>{adornment}</React.Fragment>
            ))}
         </Stack>
      );
   };
   return (
      <FormControl fullWidth error={Boolean(props.ErrorComponent)}>
         <InputLabel htmlFor={id}></InputLabel>
         <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
            <DateTimePicker
               name={campoConfig.name}
               value={editableProps.value}
               onChange={handleChange}
               label={campoConfig.label}
               minDate={campoConfig.minDate}
               maxDate={campoConfig.maxDate}
               format={FORMATS.datetime}
               views={campoConfig.views}
               disableHighlightToday={campoConfig.disableHighlightToday}
               shouldDisableDate={(date: Date | null): boolean => {
                  if (!date) return false;
                  if (campoConfig.disabledDays?.includes(date.getDay())) return true;
                  if (campoConfig.excludeDates?.some((d) => d.getTime() === date.getTime()))
                     return true;
                  if (campoConfig.disableWeekends && (date.getDay() === 0 || date.getDay() === 6))
                     return true;
                  return false;
               }}
               slotProps={{
                  textField: (params) => ({
                     ...params,
                     size: 'small',
                     InputProps: {
                        ...params.InputProps,
                        error: Boolean(props.ErrorComponent),
                        // Use the mergeAdornments function to combine both adornments
                        endAdornment: mergeAdornments(
                           errorAdornments,
                           params.InputProps?.endAdornment
                        ),
                        onBlur: handleOnBlur
                     }
                  }),
                  openPickerButton: {
                     tabIndex: -1
                  }
               }}
               formatDensity="dense"
            />
         </LocalizationProvider>
      </FormControl>
   );
}

export default CampoEditableDateTime;
